import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { navigate } from './Router';
import { useTheme } from '../contexts/ThemeContext';

const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

interface LoginProps {
  onSuccess?: () => void;
}

// インタラクティブ背景キューブコンポーネント
const InteractiveCubeBackground = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cubes, setCubes] = useState<React.ReactElement[]>([]);
  const { theme } = useTheme();

  useEffect(() => {
    const createCubes = () => {
      const cubeElements: React.ReactElement[] = [];
      const cubeSize = 30; // キューブのサイズ
      
      // 画面サイズを取得
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      
      // 敷き詰めるキューブの数を計算
      const cols = Math.ceil(screenWidth / cubeSize) + 2;
      const rows = Math.ceil(screenHeight / cubeSize) + 2;
      
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const key = `cube-${row}-${col}`;
          cubeElements.push(
            <div
              key={key}
              className={`absolute border ${
                theme === 'dark' ? 'border-zinc-700/30' : 'border-gray-200/50'
              }`}
              style={{
                width: `${cubeSize}px`,
                height: `${cubeSize}px`,
                left: `${col * cubeSize - cubeSize}px`,
                top: `${row * cubeSize - cubeSize}px`,
                backgroundColor: theme === 'dark' ? 'rgb(24, 24, 27)' : 'rgb(249, 250, 251)',
                transition: 'all 2s ease-out',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transition = 'none';
                e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgb(107, 114, 128)' : 'rgb(229, 231, 235)';
                e.currentTarget.style.borderColor = theme === 'dark' ? 'rgb(107, 114, 128)' : 'rgb(209, 213, 219)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transition = 'all 2s ease-out';
                e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgb(24, 24, 27)' : 'rgb(249, 250, 251)';
                e.currentTarget.style.borderColor = theme === 'dark' ? 'rgba(63, 63, 70, 0.3)' : 'rgba(229, 231, 235, 0.5)';
              }}
            />
          );
        }
      }
      
      setCubes(cubeElements);
    };

    createCubes();
    
    // ウィンドウリサイズ時に再計算
    const handleResize = () => createCubes();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, [theme]);

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 overflow-hidden pointer-events-none"
      style={{ zIndex: 1 }}
    >
      <div className="relative w-full h-full">
        {cubes.map((cube, index) => (
          <div key={index} className="pointer-events-auto">
            {cube}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Login({ onSuccess }: LoginProps) {
  const { login } = useAuth();
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await login();
      onSuccess?.();
    } catch (error: any) {
      const errorMessage = error.message || 'Sign in failed. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-theme-primary flex items-center justify-center p-4 relative">
      {/* インタラクティブキューブ背景 */}
      <InteractiveCubeBackground />
      
      {/* Back to Home link */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 text-brand-primary hover:text-brand-hover transition-colors text-sm z-50"
      >
        ← Back to Home
      </button>
      
      <div className="w-full max-w-md relative z-10">
        <div className={`${
          theme === 'dark' 
            ? 'bg-zinc-800/50 border-zinc-700/50' 
            : 'bg-white/80 border-gray-200'
        } backdrop-blur-lg rounded-2xl shadow-2xl border p-8`}>
          {/* Logo/Title */}
          <div className="text-center mb-8">
            <button 
              onClick={() => navigate('/')}
              className="group transition-all duration-200 hover:scale-105 active:scale-95"
            >
              <h1 className="text-3xl sm:text-4xl fira-sans-extra-bold-italic text-brand-primary mb-2 group-hover:text-brand-hover transition-colors">
                pixel moment
              </h1>
            </button>
            <p className="text-theme-tertiary text-sm">
              Transform your moments into pixel art
            </p>
          </div>

          {/* Sign In Form */}
          <div className="space-y-6">

            {error && (
              <div className={`${
                theme === 'dark'
                  ? 'bg-red-500/10 border-red-500/30'
                  : 'bg-red-50 border-red-200'
              } border rounded-lg p-3`}>
                <p className={`${
                  theme === 'dark' ? 'text-red-400' : 'text-red-600'
                } text-sm text-center`}>{error}</p>
              </div>
            )}

            <button
              onClick={handleLogin}
              disabled={isLoading}
              className={`w-full flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                isLoading
                  ? theme === 'dark' 
                    ? 'bg-zinc-700 cursor-not-allowed text-zinc-400'
                    : 'bg-gray-200 cursor-not-allowed text-gray-400'
                  : theme === 'dark'
                    ? 'bg-white hover:bg-gray-100 text-gray-900'
                    : 'bg-gray-900 hover:bg-gray-800 text-white'
              } hover:scale-105 active:scale-95 shadow-lg`}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className={`w-5 h-5 border-2 ${
                    theme === 'dark' ? 'border-zinc-500' : 'border-gray-400'
                  } border-t-transparent rounded-full animate-spin mr-2`}></div>
                  Signing In...
                </div>
              ) : (
                <>
                  <GoogleIcon />
                  Sign In with Google
                </>
              )}
            </button>

            <div className="text-center text-xs text-theme-tertiary">
              By signing in, you can save your creations<br />
              and share them with other users.
            </div>
          </div>
        </div>

        {/* Bottom text */}
        <div className="text-center mt-6 text-theme-tertiary text-xs">
          © 2025 pixel moment. Every moment into pixel art.
        </div>
      </div>
    </div>
  );
}
